import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Dashboard from './Dashboard';
import LoginPage from './LoginPage';
import ICDSearch from './ICDSearch';
import ICDCategories from './ICDCategories';
import AdminTools from './AdminTools';

// Main App component that sets up the router
const App = (props) => {
  // Get props from either direct props or window.appProps
  const serverProps = props || (typeof window !== 'undefined' ? window.appProps : {}) || {};
  
  return (
    <BrowserRouter>
      <AppContent serverProps={serverProps} />
    </BrowserRouter>
  );
};

// Separate component to use hooks inside the Router context
const AppContent = ({ serverProps }) => {
  console.log('AppContent received serverProps:', serverProps);
  
  // Convert snake_case api_endpoints to camelCase apiEndpoints
  const apiEndpoints = (() => {
    console.log('Processing serverProps.api_endpoints:', serverProps.api_endpoints);
    
    if (!serverProps.api_endpoints) {
      console.warn('No api_endpoints found in serverProps!');
      return null;
    }
    
    console.log('Original API endpoints:', serverProps.api_endpoints);
    
    // Create a properly transformed object with camelCase keys
    const transformed = {
      users: serverProps.api_endpoints.users,
      toggleAdmin: serverProps.api_endpoints.toggle_admin,
      icdCodes: serverProps.api_endpoints.icd_codes,
      uploadIcd: serverProps.api_endpoints.upload_icd,
      refreshIcd: serverProps.api_endpoints.refresh_icd
    };
    
    console.log('Transformed apiEndpoints:', transformed);
    return transformed;
  })();
  
  console.log('Transformed apiEndpoints:', apiEndpoints);
  
  // Force conversion to boolean to ensure correct state
  const initialAuthenticated = Boolean(serverProps.authenticated);
  const [authenticated, setAuthenticated] = useState(initialAuthenticated);
  const [currentUser, setCurrentUser] = useState(serverProps.current_user || null);
  const [isAdmin, setIsAdmin] = useState(currentUser ? currentUser.is_admin : false);
  const [loading, setLoading] = useState(false);
  
  const location = useLocation();
  
  // Cleanup URL if it contains debug parameters
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (url.searchParams.has('auth') || url.searchParams.has('debug_session_id')) {
        url.searchParams.delete('auth');
        url.searchParams.delete('debug_session_id');
        window.history.replaceState({}, document.title, url.toString());
      }
    }
  }, []);
  
  // Fetch authentication state directly from the server
  const checkAuthState = async () => {
    try {
      setLoading(true);
      const response = await fetch('/auth_debug');
      const data = await response.json();
      
      // Update state based on server response
      const isAuthenticated = Boolean(data.user_signed_in);
      setAuthenticated(isAuthenticated);
      setCurrentUser(data.current_user || null);
      setIsAdmin(data.current_user ? data.current_user.admin : false);
    } catch (error) {
      console.error('Error checking auth state:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Check auth state on mount
  useEffect(() => {
    checkAuthState();
  }, []);
  
  const handleLogout = () => {
    // Redirect to the server-side logout route
    window.location.href = serverProps.sign_out_url || '/sso_session/signout';
  };

  // Get the current active nav item based on the URL path
  const getCurrentNav = () => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return 'dashboard';
    if (path === '/search') return 'icd-search';
    if (path === '/categories') return 'icd-categories';
    if (path === '/admin') return 'admin-tools';
    return 'dashboard';
  };
  
  return (
    <div className="app min-vh-100 d-flex flex-column">
      <Navbar 
        authenticated={authenticated} 
        currentUser={currentUser} 
        isAdmin={isAdmin}
        onLogout={handleLogout}
        activeNav={getCurrentNav()}
      />
      
      <main className="flex-grow-1 bg-light">
        <div className="container py-4">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-2">Checking authentication...</p>
            </div>
          ) : (
            <Routes>
              {!authenticated ? (
                <>
                  <Route 
                    path="*" 
                    element={<LoginPage authUrl={serverProps.auth_url || '/sso_session/entra_id'} />} 
                  />
                </>
              ) : (
                <>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route 
                    path="/dashboard" 
                    element={<Dashboard isAdmin={isAdmin} apiEndpoints={apiEndpoints} />} 
                  />
                  <Route path="/search" element={<ICDSearch />} />
                  <Route path="/categories" element={<ICDCategories />} />
                  <Route 
                    path="/admin" 
                    element={
                      isAdmin ? (
                        <AdminTools apiEndpoints={apiEndpoints} />
                      ) : (
                        <div className="alert alert-danger">
                          You don't have permission to access this area.
                        </div>
                      )
                    } 
                  />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </>
              )}
            </Routes>
          )}
        </div>
      </main>
      
      <footer className="bg-dark text-light py-3">
        <div className="container">
          <div className="row">
            <div className="col-md-6">
              <p className="mb-0">
                <i className="fas fa-laptop-medical mr-2"></i>
                Kipu ICD Service &copy; {new Date().getFullYear()}
              </p>
            </div>
            <div className="col-md-6 text-md-right">
              <p className="mb-0">
                <small>Providing ICD code lookup services</small>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App; 
